import type { BuybackListing } from '../types/buybackListing';

const esc = (s: string | null | undefined): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));

const won = (n: number | null | undefined): string =>
  n == null ? '가격 문의' : `${n.toLocaleString('ko-KR')}원`;

const cardHtml = (it: BuybackListing): string => `
    <div class="card">
      <div class="imgbox">
        ${it.imageUrl
          ? `<img src="${esc(it.imageUrl)}" alt="${esc(it.productName)}" />`
          : `<div class="noimg">이미지 없음</div>`}
      </div>
      <div class="name">${esc(it.productName)}</div>
      <div class="no">${esc(it.cardNumber ?? '')}</div>
      <div class="price">${won(it.price)}</div>
    </div>`;

/**
 * 매입 리스트를 A4 인쇄용 HTML 로 새 창에 그려 바로 인쇄 대화상자를 띄운다. 손님에게 보여
 * 주는 게시물이라(관리 화면과 달리) **상품 이미지가 크게·눈에 띄게** 나오도록 카드 그리드로
 * 배치한다. 제목·매장명 등 머리말 문구는 넣지 않는다(2026-09-10 요청).
 *
 * 레이아웃(2026-09-10 재정비): CSS Grid 4열(열 폭은 grid 가 정확히 나눠 折り返し 없음) +
 * 낮은 고정 행 높이(56mm) + 브라우저 자연 改頁. 페이지 높이를 억지로 채우지(1fr) 않고
 * 시트 높이도 고정하지 않는다 — 그게 인쇄 여백 설정에 따라 실제 인쇄 영역보다 커져 카드가
 * 페이지 경계에서 잘리는 원인이었다(2026-09-10 실측). 행 높이가 한 페이지보다 훨씬 작고
 * `break-inside: avoid` 라 카드는 절대 잘리지 않고, 한 페이지에 들어갈 행 수는 실제 인쇄
 * 영역에 맞춰 자동으로 정해진다(보통 여백이면 4행=16개). inline-block 은 % 폭이 端数로
 * 어긋나 4열째가 折り返し되는 문제가 있어 쓰지 않는다.
 */
export function printBuybackList(items: BuybackListing[]): boolean {
  const cards = items.map(cardHtml).join('');

  const html = `<!doctype html>
<html lang="ko"><head><meta charset="utf-8" />
<title>매입 리스트</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Pretendard', -apple-system, 'Malgun Gothic', sans-serif; color: #1a1a1a; margin: 0; }
  /* 4열: grid 가 폭을 정확히 4등분하므로 折り返し이 없다. 행 높이 64mm 고정 → 4행(=16개)이
     4*64 + 3*4(gap) = 268mm. 인쇄 여백을 '최소(Minimum)'로 쓰는 전제라 A4 인쇄 영역이
     277mm 이상 확보돼(2026-09-10 사용자 확인) 268mm 는 넉넉히 한 페이지에 들어가면서도
     아래 여백을 최소화한다. @page margin 10mm 로 277mm 상한을 넘지 않게 잡아, 여백을 조금 더
     크게 잡더라도 4행째가 2페이지로 넘치지 않는다. 시트 높이·1fr 채우기는 쓰지 않는다. */
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); grid-auto-rows: 64mm; gap: 4mm; }
  /* break-inside:avoid → 카드가 페이지 경계에서 절반만 잘리지 않고 통째로 다음 장으로 넘어간다. */
  .card { border: 1px solid #e5e5e5; border-radius: 6px; overflow: hidden;
          break-inside: avoid; page-break-inside: avoid;
          display: flex; flex-direction: column; min-height: 0; }
  /* 이미지가 카드의 대부분(세로 공간을 최대한 차지) */
  .imgbox { flex: 1 1 auto; min-height: 0; background: #f7f7f7;
            display: flex; align-items: center; justify-content: center; }
  .imgbox img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .noimg { font-size: 11px; color: #aaa; }
  .name { flex: 0 0 auto; font-size: 12px; font-weight: 600; line-height: 1.25; padding: 4px 6px 1px;
          text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  /* 고유번호도 손님이 또렷이 읽을 수 있게 — 크고 진하게. */
  .no { flex: 0 0 auto; font-size: 13px; font-weight: 700; color: #222; padding: 1px 6px;
        text-align: center; letter-spacing: .02em; }
  .price { flex: 0 0 auto; font-size: 16px; font-weight: 800; color: #c0392b; padding: 2px 6px 6px; text-align: center; }
  @page { size: A4 portrait; margin: 10mm; }
</style></head>
<body>
  <div class="grid">${cards}</div>
<script>window.onload = function () { window.print(); };</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=900,height=1200');
  if (!w) return false; // 팝업 차단 — 호출부에서 안내
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
